// The persistent lens chrome (§46, §46.1 P1) — ONE frame that owns the top
// bar, the timeline and the bottom-panel shell for every lens page. Scenes
// render beneath it; pages contribute a bar section and panel content and
// otherwise read the frame's measurements instead of computing their own.
//
// Lens VALUES live in the provider (§46.4 inventory) — this file never keeps a
// copy of a lens dimension, which is the whole point: the lensCarry drift bug
// class came from copies-with-sync. What the shell DOES own is chrome state
// the lens has no opinion about: panel mode, measured heights, the insets
// derived from them (§46.3 d4 PANEL contract, lensShellContract.md).
//
// §48.2 B — MOUNTED ONCE, above <Routes> (Main.jsx), wrapping the routed
// scenes for lens routes only. The chrome no longer disappears and reappears
// on navigation: the bar, the timeline and the PANEL MODE survive the page
// swap because the SHELL holds them and the shell is never unmounted — not
// because every frame below it survives: PanelFrame is mounted only for a page
// that registers panelContent (the map does not), so panel MODE persists as
// shell state (adopt-once, below), not as a living component. Pages do not
// render this file any more — they REGISTER their contributions with
// useLensPageChrome() on mount and deregister on unmount, and the scenes
// arrive here as `children`.
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ChevronDown,
  ChevronUp,
  Globe,
  MapPin,
  Maximize2,
  Minimize2,
  Plus,
  Save,
} from "lucide-react";

import CurationSelector from "./CurationSelector";
import LensControl from "./LensControl";
import LensSearchInput from "./LensSearchInput";
import LensToolbar from "./LensToolbar";
import SourceSelector from "./SourceSelector";
import TimelineStrip from "./TimelineStrip";
import useLensTopbarVar from "./useLensTopbarVar";
import { useLens, useLensPage } from "./LensProvider";

// Chrome geometry. The GS top navbar is REMOVED (NAVBAR_REMOVAL_PLAN.md):
// the clearance is now just a 12px breathing gap (birr's §4.2 ruling), and
// the toolbar sits at the top of the scene with the floating sidebar toggle
// in its left gutter. EXPORTED because OsintMapHome computes its own copy of
// this inset — one constant, no drift.
export const NAV_CLEARANCE = 12;

// iso-fork: the lens PRESET machinery (lens dropdown, new/save, trending
// override + mode switch, event-type and region selectors) is GS-only UI.
// Other variants keep the slim bar — search, curation, and the page's own
// controls (refresh, entity types, reset view). State/endpoints unchanged;
// this hides chrome only.
const isGSVariant = process.env.REACT_APP_IS_GS === "true";
const TOOLBAR_GAP = 16;
// FIRST PAINT ONLY: the strip reports its real band (card + brush readout
// lane) as soon as it is measured, and the band changes with the brush and
// with a wrapped range row. Nothing may reserve this number permanently.
const TIMELINE_BAND_FALLBACK = 64;
const SCENE_GAP = 8;

// Controlled two-row wrap (SIDEBAR_LENS_REWORK): when the row can no longer
// fit every control PLUS a minimum-width search, the search input — and only
// the search input — moves to a full-width second row. Without this the
// row's flex-wrap breaks whatever control happens to overflow, which reads
// as chrome falling apart rather than a designed second row.
const SEARCH_MIN_PX = 160; // LensSearchInput's own min-w-[160px]
const SEARCH_SPLIT_GAP_PX = 8; // the row's gap-2
// Rejoin only with this much surplus beyond "barely fits". At the promote
// moment controls may be measured SHRUNK (min-w-0 wrappers); after promotion
// they relax to natural width, so `needed` can only grow — the band absorbs
// that shrunk-vs-natural delta and keeps the boundary from oscillating.
const SEARCH_SPLIT_HYSTERESIS_PX = 48;
const PEEK_CONTENT_PX = 304; // 2 tile rows, the map's peek
const EXPANDED_VH = 55;
const PANEL_CHROME_PX = 8; // header + padding above the content box

const PANEL_MODES = ["closed", "peek", "expanded"];

// The §46.1 P2 lens edge, read from the row: only "system" feed views are event
// types — a favorited source or a custom view is a MAP PAGE dimension riding
// the same dropdown. A page whose event-type rows carry no kind at all (the
// graph) has no page half. This is what tells the selector whether `Clear` is
// its own action or a second name for `All`.
const isFeedViewPageRow = (source) =>
  Boolean(source?.feedViewKind) && source.feedViewKind !== "system";

// ── frame context ───────────────────────────────────────────────────────────
// Everything a scene needs to lay itself out under the chrome. Since §48.2 B
// the scenes render INSIDE the shell, so they read this by context —
// directly, or through <LensSceneFrame> for a scene that wants the render-prop
// shape (which keeps the page component itself off the frame subscription, so
// a frame change re-renders the scene and nothing above it).
const LensFrameContext = createContext({
  width: 0,
  height: 0,
  topBarH: 56,
  panelH: 0,
  panelFootprint: 0,
  panelMode: "peek",
  setPanelMode: () => {},
  timelineVisible: false,
  topInset: NAV_CLEARANCE + 56 + TOOLBAR_GAP,
  bottomInset: SCENE_GAP,
});

export const useLensFrame = () => useContext(LensFrameContext);

// Render-prop consumer of the frame. A page renders this where its scene used
// to sit in `sceneChildren`, so the scene body is unchanged and the PAGE stays
// off the frame subscription.
export const LensSceneFrame = ({ children }) => {
  const frame = useLensFrame();
  return typeof children === "function" ? children(frame) : children || null;
};

// ── page-chrome registration (§48.2 B) ──────────────────────────────────────
// The registrar is stable for the shell's whole life; only the shell re-renders
// when a registration lands, which is why publishing cannot loop (see the hook).
const LensChromeContext = createContext(null);

let chromeSeq = 0;

// A page's contribution to the once-mounted chrome: the SAME bag it used to
// pass as LensShell props (lensShellContract.md is the list), minus the ones
// the shell now owns for everyone (`sceneChildren` — scenes are `children` of
// the shell now — and `className`).
//
// Registration is also how the page names itself for the memory engine: the
// shell outlives every page, so it can no longer answer "which scene is
// mounted".
export const useLensPageChrome = (contribution) => {
  const registrar = useContext(LensChromeContext);
  const idRef = useRef(0);
  if (!idRef.current) idRef.current = ++chromeSeq;
  const id = idRef.current;

  useLensPage(contribution?.page || null);

  // Published on EVERY commit, not just mount: the bag carries this render's
  // nodes and handlers. It cannot loop — the shell's own re-render leaves its
  // `children` element identity untouched, so the page subtree bails out and
  // never publishes again. Cost is parity with the per-page mount (one shell
  // render per page render, exactly as when the page rendered the shell).
  // LAYOUT effect: the swap must be committed before the browser paints.
  useLayoutEffect(() => {
    registrar?.publish(id, contribution || null);
  });

  // Deregistration is id-guarded (the provider's registerPage pattern): an exit
  // animation can unmount the OLD page after the next one registered, and an
  // ungated retract would blank live chrome.
  useLayoutEffect(() => () => registrar?.retract(id), [registrar, id]);
};

const EMPTY_CHROME = {};

// Route-transition gap: the page-section slot keeps its width so the core bar
// cannot reflow while nothing is registered (INSTANT-SWAP ruling — content
// swaps, layout never does). FIXED width, not flex-1: in the one-row bar the
// search input is the designated flexer, and a stretching placeholder would
// fight it for the slack mid-gap. Sized to the feed's section (the only page
// registering one: sources selector + sort toggle).
const PAGE_SECTION_PLACEHOLDER = (
  <div aria-hidden className="w-[272px] shrink" />
);

const toArray = (value) => (value ? [...value] : []);
// Membership equality: a selector hands back its ROW order, which is not the
// order the lens happens to hold — an ordered compare would report every
// redundant click as a change.
const sameSelection = (a, b) => {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((id) => set.has(id));
};
// Provider values may be Sets (graph vocabulary) or arrays; write back in
// whatever shape the provider already holds so the shell never dictates it.
const likeShape = (current, next) =>
  current instanceof Set ? new Set(next) : next;

const render = (slot, ...args) =>
  typeof slot === "function" ? slot(...args) : slot;

// ── panel frame ─────────────────────────────────────────────────────────────
// Owns the mode ladder, the animated content box, height reporting and the
// mode-change camera delta. Page content is a render prop and knows none of
// this (§46.3 d4).
const PanelFrame = ({
  mode,
  onModeChange,
  visible,
  header,
  content,
  scope,
  onHeightChange,
  onCameraShift,
  staticBar = false,
}) => {
  const rootRef = useRef(null);
  const contentRef = useRef(null);

  // STATIC BAR (`panelStatic`, the feed's stats strip): a header-only bar
  // under the timeline that never opens — no mode buttons, no content box.
  // The frame still mounts, measures, and reports height, so bottomInset
  // keeps reserving its footprint exactly like a closed panel's.
  const effMode = staticBar ? "closed" : mode;

  // `visible` MUST stay a dep: the frame returns null pre-data, so an observer
  // attached then binds to nothing and the timeline hides under a stale height
  // until a mode toggle re-runs the effect (the Phase 15 bug, twice shipped).
  useEffect(() => {
    if (!visible) {
      onHeightChange(0);
      return undefined;
    }
    const el = rootRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    onHeightChange(el.getBoundingClientRect().height);
    const ro = new ResizeObserver(() =>
      onHeightChange(el.getBoundingClientRect().height),
    );
    ro.observe(el);
    return () => ro.disconnect();
  }, [onHeightChange, effMode, visible]);

  // Camera courtesy: report the footprint delta ONCE per mode change. The
  // animated height would feed the observer fractional deltas the scene cannot
  // tell from jitter (§22.5).
  const prevModeRef = useRef(effMode);
  useEffect(() => {
    const prev = prevModeRef.current;
    prevModeRef.current = effMode;
    if (prev === effMode || !onCameraShift) return;
    const footprintFor = (m) => {
      if (m === "closed") return 0;
      const box =
        m === "expanded"
          ? window.innerHeight * (EXPANDED_VH / 100)
          : PEEK_CONTENT_PX;
      return box + PANEL_CHROME_PX;
    };
    onCameraShift(footprintFor(effMode) - footprintFor(prev));
  }, [effMode, onCameraShift]);

  // Scroll-to-expand (map behavior): wheel-down over peek expands; wheel-up at
  // the top of the expanded content collapses back. The page's scroller can be
  // at ANY depth (the graph's tile grid is two levels down), so "at the top"
  // is answered by walking the event's ancestor chain — a fixed
  // firstElementChild probe collapses the panel mid-scroll.
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return undefined;
    const scrolledInside = (target) => {
      for (let node = target; node && node !== el; node = node.parentElement) {
        if (node.scrollTop > 0) return true;
      }
      return el.scrollTop > 0;
    };
    const onWheel = (event) => {
      if (effMode === "peek" && event.deltaY > 0) {
        event.preventDefault();
        onModeChange("expanded");
      } else if (
        effMode === "expanded" &&
        event.deltaY < 0 &&
        !scrolledInside(event.target)
      ) {
        event.preventDefault();
        onModeChange("peek");
      }
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [effMode, onModeChange]);

  if (!visible) return null;

  const contentHeight =
    effMode === "closed"
      ? 0
      : effMode === "expanded"
        ? `${EXPANDED_VH}vh`
        : `${PEEK_CONTENT_PX}px`;

  return (
    // FLOATING PANEL (matches the top bar's language): 16px side + bottom
    // gaps and rounded glass instead of the old flush full-width slab.
    // rootRef moved to THIS wrapper so the reported height includes the
    // bottom gap — the timeline strip and scene inset stack above it
    // without overlapping.
    <div
      ref={rootRef}
      className="pointer-events-none absolute inset-x-0 bottom-0 z-[995] px-4 pb-4"
    >
      <div
        className="pointer-events-auto w-full rounded-xl border border-mainDivider bg-mainBg/95 p-2 font-poppins text-mainText backdrop-blur-md"
        style={{
          backgroundImage:
            "radial-gradient(ellipse 80% 60% at 50% 0%, rgba(14,165,233,0.06) 0%, transparent 100%)",
          boxShadow:
            "0 0 0 1px rgba(14,165,233,0.06), 0 0 12px 2px rgba(14,165,233,0.08), 0 22px 48px rgba(0,0,0,0.35)",
        }}
      >
        <div
          className={`flex items-center justify-between gap-2 rounded-lg border border-mainDivider bg-mainButtonBg/30 px-3 py-1.5 ${
            effMode === "closed" ? "" : "mb-2"
          }`}
        >
          <div className="flex min-w-0 flex-1 items-center gap-2">
            {render(header, effMode, scope)}
          </div>
          {!staticBar && (
            <div className="flex shrink-0 items-center gap-1">
              {effMode !== "closed" && (
                <button
                  type="button"
                  onClick={() => onModeChange(effMode === "expanded" ? "peek" : "expanded")}
                  title={effMode === "expanded" ? "Collapse panel" : "Expand panel"}
                  aria-label={effMode === "expanded" ? "Collapse panel" : "Expand panel"}
                  className="rounded-md p-1 text-mutedText transition-all duration-150 hover:text-velBlue"
                >
                  {effMode === "expanded" ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
                </button>
              )}
              <button
                type="button"
                onClick={() => onModeChange(effMode === "closed" ? "peek" : "closed")}
                title={effMode === "closed" ? "Open panel" : "Close panel"}
                aria-label={effMode === "closed" ? "Open panel" : "Close panel"}
                className="rounded-md p-1 text-mutedText transition-all duration-150 hover:text-velBlue"
              >
                {effMode === "closed" ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
              </button>
            </div>
          )}
        </div>

        <div
          ref={contentRef}
          className="overflow-hidden transition-[height] duration-300 ease-in-out"
          style={{ height: contentHeight }}
        >
          {effMode !== "closed" && render(content, effMode, scope)}
        </div>
      </div>
    </div>
  );
};

// ── the shell ───────────────────────────────────────────────────────────────
// Props are only what the MOUNT POINT knows: the routed scenes and a class.
// Everything a page contributes arrives through the registry below.
const LensShell = ({ children, className = "" }) => {
  const [registration, setRegistration] = useState(null);
  const registrar = useMemo(
    () => ({
      // Both directions are id-guarded, and for the same reason: ids are
      // monotonic, so a LOWER id is a page the router already left. An exiting
      // page that re-renders before its unmount lands must not clobber the
      // chrome the arriving page just published.
      publish: (id, bag) =>
        setRegistration((cur) =>
          cur && cur.id > id ? cur : { id, bag: bag || EMPTY_CHROME },
        ),
      retract: (id) =>
        setRegistration((cur) => (cur && cur.id === id ? null : cur)),
    }),
    [],
  );
  const registered = registration != null;
  const chrome = registration?.bag || EMPTY_CHROME;
  const {
    // top bar
    presetProps = null, // spread over the derived LensControl bag (rows/handlers)
    searchPlaceholder = "Search",
    searchLoading = false,
    onSearchSubmit,
    controlsExtra = null, // trailing controls-row nodes (refresh, …)
    eventTypeSources = [], // [{id, label, icon, color, targetTypes}]
    regionSources = [],
    onRegionSearchChange,
    // CORE-CONTROL SEAMS (§48.2 A). Spread LAST over the matching core control,
    // so a page whose dimension still speaks its own dialect (the map's
    // feed-view ids / environment region ids, its local search) keeps its
    // wiring while the SHELL owns the markup. A page that passes none gets pure
    // core — that is the target state; a bag is a staged adoption, not a new
    // home for a lens value.
    searchProps = null,
    eventTypeSelectorProps = null,
    regionSelectorProps = null,
    // Bar height for a page that positions its own overlays off the bar (the
    // map). Scenes that read the frame use topInset instead.
    onTopBarHeightChange,
    selectorOverride = null, // replaces the CORE selectors (trending row)
    // Controls that belong TO the override and must sit with it — the trending
    // mode switch. They render in BOTH branches (the switch is how a page gets
    // back to the resolved set once the lens names its own types, so it cannot
    // vanish when the override steps aside), but always in the override's own
    // cell rather than at the row's end, where two other controls separated a
    // switch from the label it belongs to.
    selectorLead = null,
    selectorExtras = null, // chips riding the selector row (focus, reset view)
    pageSection = null, // the page's own bar controls (§46.3 d4 PAGE-SECTION)
    // timeline
    showTimeline = true,
    // {events, serverTimeline, rangeLocked, onPlaybackChange, onSelectEvent,
    // selectedEvent, playbackStopSignal}. `rangeLocked` is for a GENUINE lock
    // only — trending resolves event types and nothing else, so the lens's own
    // window is what filters under it and must stay editable.
    timeline = null,
    // panel
    panelVisible = false,
    initialPanelMode = "peek",
    onPanelModeChange,
    panelScope = null, // {selection, window} — see the two-level rule
    panelHeader = null, // node | (mode, scope) => node
    panelContent = null, // node | (mode, scope) => node
    // STATIC BAR: header-only strip under the timeline that never opens (no
    // mode buttons, no content box) — the feed's stats strip. Mounts the
    // frame on panelHeader alone; panelContent stays the opt-in for a REAL
    // panel and wins if both are supplied.
    panelStatic = false,
    onPanelCameraShift,
    // every CORE lens write calls this first (graph: discardActionSnapshot)
    onLensEdit,
  } = chrome;

  const {
    eventTypes,
    setEventTypes,
    regions,
    setRegions,
    regionNames,
    setRegionNames,
    timeRange,
    setTimeRange,
    customDateRange,
    setCustomDateRange,
    search,
    setSearch,
    curation,
    setCuration,
    presets,
    activePreset,
    modified,
  } = useLens();

  const rootRef = useRef(null);
  const topBarRef = useRef(null);
  const [dims, setDims] = useState({ width: 0, height: 0 });

  // The bar's MEASURED height: observed, published as --lens-topbar-h and
  // stashed to localStorage by the shared hook (useLensTopbarVar — also used
  // by /threads' ThreadsTopBar, the only other bar). Chrome outside the shell
  // (the sidebar toggle and the right aside's top edge, Main.jsx) reads the
  // var; the shell itself feeds topInset from the returned number.
  const topBarH = useLensTopbarVar(topBarRef, {
    onChange: onTopBarHeightChange,
  });
  const [panelH, setPanelH] = useState(0);
  const [timelineH, setTimelineH] = useState(TIMELINE_BAND_FALLBACK);
  const [panelMode, setPanelModeState] = useState(initialPanelMode);

  // Sub-pixel reports are jitter, not a layout change; swallowing them keeps a
  // fractional height from re-rendering every frame consumer. Stable identity —
  // the strip re-measures whenever this callback changes.
  const handleTimelineHeight = useCallback((height) => {
    setTimelineH((current) =>
      Math.abs(current - height) < 0.5 ? current : height,
    );
  }, []);

  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    setDims({ width: el.offsetWidth, height: el.offsetHeight });
    const ro = new ResizeObserver(() =>
      setDims({ width: el.offsetWidth, height: el.offsetHeight }),
    );
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Controlled two-row wrap (constants at top): would every non-search
  // control at its CURRENT width, plus gaps, plus a minimum-width search,
  // fit the row? Promote (split) the moment it wouldn't; demote only with
  // hysteresis surplus. Evaluated on every commit (content changes width
  // without resizing anything) and on row resizes; setState is
  // equality-guarded so the passes are free when nothing flips.
  const rowRef = useRef(null);
  const searchWrapRef = useRef(null);
  const [searchSplit, setSearchSplit] = useState(false);
  const evaluateSearchSplit = useCallback(() => {
    const row = rowRef.current;
    const searchWrap = searchWrapRef.current;
    if (!row || !searchWrap) return;
    let others = 0;
    let gaps = 0;
    for (const child of row.children) {
      if (child === searchWrap) continue;
      others += child.offsetWidth;
      gaps += 1;
    }
    const needed = others + gaps * SEARCH_SPLIT_GAP_PX + SEARCH_MIN_PX;
    const avail = row.clientWidth;
    setSearchSplit((current) =>
      current ? needed + SEARCH_SPLIT_HYSTERESIS_PX > avail : needed > avail,
    );
  }, []);
  useLayoutEffect(() => {
    evaluateSearchSplit();
  });
  useEffect(() => {
    const el = rowRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(evaluateSearchSplit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [evaluateSearchSplit]);

  const setPanelMode = useCallback(
    (next) => {
      if (!PANEL_MODES.includes(next)) return;
      setPanelModeState(next);
      onPanelModeChange?.(next);
    },
    [onPanelModeChange],
  );

  // A page's stored panel preference arrives AFTER mount (server hydration is
  // async), so the FIRST late change to initialPanelMode is adopted. It cannot
  // fight the user: the page echoes back the value the ladder just set, and
  // this path never calls onPanelModeChange.
  //
  // §48.2 B — adopted ONCE per shell life, and the shell now outlives every
  // page. Later changes are the next page mounting with its own un-hydrated
  // default, which must NOT reach in and reset a mode the user set on the page
  // before: mode persistence across navigation is the point of the lift.
  const initialModeRef = useRef(initialPanelMode);
  const modeAdoptedRef = useRef(false);
  useEffect(() => {
    if (initialPanelMode === initialModeRef.current) return;
    initialModeRef.current = initialPanelMode;
    if (modeAdoptedRef.current) return;
    modeAdoptedRef.current = true;
    if (PANEL_MODES.includes(initialPanelMode)) setPanelModeState(initialPanelMode);
  }, [initialPanelMode]);

  // §48.2 C — the playback FORWARDER. It no longer writes the provider: the
  // strip owns the brush and writes it directly (one writer, no way for a
  // report echo to collapse a range to its leading edge). What survives is the
  // stable identity — the strip re-reports whenever this callback changes, so
  // an inline wrapper would fire a report per shell render.
  const playbackCbRef = useRef(null);
  const livePlaybackCb = timeline?.onPlaybackChange || null;
  // COMMITTED renders only, like the gap hold. Safe because every report the
  // strip makes comes from a PASSIVE effect, and a child's passive effects run
  // after this parent layout effect — no report can reach a stale handler.
  useLayoutEffect(() => {
    playbackCbRef.current = livePlaybackCb;
  }, [livePlaybackCb]);
  const handlePlaybackReport = useCallback((report) => {
    playbackCbRef.current?.(report);
  }, []);

  // ── core lens writes ──────────────────────────────────────────────────────
  const beginEdit = useCallback(() => onLensEdit?.(), [onLensEdit]);

  // The event-type control shows the LENS and nothing else. The server's
  // resolved trending filter (the echo) is display-only and never reaches this
  // control: what a page shows in place of it under trending is that page's own
  // row, and the row presents the resolution itself (lensShellContract.md).
  const selectedEventTypes = useMemo(() => toArray(eventTypes), [eventTypes]);

  const handleEventTypesChange = useCallback(
    (next, intent) => {
      // Checking every box = reset to unfiltered — "All" stays a reset action,
      // not an enumeration of the vocabulary (map semantics). The selector
      // presents that empty set as every row checked, so the reset does not
      // read as a blanked control.
      const value =
        intent?.action === "all" || next.length >= eventTypeSources.length
          ? []
          : next;
      // A click that changes nothing writes NOTHING: mounting, a re-render and
      // a no-op click must never author a value the user did not choose.
      if (sameSelection(toArray(eventTypes), value)) return;
      beginEdit();
      setEventTypes(likeShape(eventTypes, value));
    },
    [beginEdit, eventTypeSources, eventTypes, setEventTypes],
  );

  const selectedRegions = useMemo(() => toArray(regions), [regions]);

  const handleRegionsChange = useCallback(
    (next, intent) => {
      // "All regions" is UNFILTERED, never an enumeration: the vocabulary is
      // ~300 countries against a server cap, and with a dropdown search open
      // the enumeration would silently mean "all currently matching".
      const value = intent?.action === "all" ? [] : next;
      if (sameSelection(toArray(regions), value)) return;
      beginEdit();
      setRegions(likeShape(regions, value));
      // Names are carried beside the ids so a saved lens can label a region it
      // can no longer find in the (search-scoped) options list — and are pruned
      // on deselect, or the map grows past the cap and evicts live labels.
      setRegionNames?.((prev) => {
        const map = prev instanceof Map ? new Map(prev) : new Map(Object.entries(prev || {}));
        const kept = new Set(value);
        for (const id of [...map.keys()]) {
          if (!kept.has(id)) map.delete(id);
        }
        for (const id of value) {
          const hit = regionSources.find((source) => source.id === id);
          if (hit) map.set(id, hit.label);
        }
        return prev instanceof Map || prev == null ? map : Object.fromEntries(map);
      });
    },
    [beginEdit, regions, setRegions, setRegionNames, regionSources],
  );

  const handleTimeRangeChange = useCallback(
    (key) => {
      beginEdit();
      setTimeRange(key);
      setCustomDateRange(null);
    },
    [beginEdit, setTimeRange, setCustomDateRange],
  );

  const handleCustomRangeApply = useCallback(
    (range) => {
      beginEdit();
      setTimeRange("custom");
      setCustomDateRange(range);
    },
    [beginEdit, setTimeRange, setCustomDateRange],
  );

  const handleSearchChange = useCallback((value) => setSearch(value), [setSearch]);

  // §46.1 P3: a curation change IS a lens change — same beginEdit hook, same
  // provider write, so pages refetch through the lens reactions they already
  // have. Re-clicking the active state clears back to "All" (single-select
  // with no dead click).
  const handleCurationChange = useCallback(
    (next) => {
      beginEdit();
      setCuration(curation === next ? null : next || null);
    },
    [beginEdit, curation, setCuration],
  );

  // ── frame ─────────────────────────────────────────────────────────────────
  // THE GAP (§48.2 B): <AnimatePresence mode="wait"> unmounts the leaving page
  // before the arriving one registers, so for a beat nothing is registered.
  // Content is allowed to swap instantly; GEOMETRY is not — the last
  // registration's footprint stands until the next one lands, the page-section
  // slot holds its width with a placeholder, and the bar fields that decide the
  // selector row's child count are held as they were (heldBar, below).
  const gapRef = useRef({
    hadSection: false,
    timelineVisible: false,
    panelFootprint: 0,
    bar: null,
  });
  const liveTimelineVisible = showTimeline && !!timeline;
  // No panelContent = no PANEL AT ALL (§48.2 A): the frame is not mounted and
  // reserves no inset, so a page that still owns its bottom panel (the map,
  // until the panel phase) is not covered by an empty second one.
  const panelEnabled =
    registered && (panelContent != null || (panelStatic && panelHeader != null));
  const livePanelFootprint = panelEnabled && panelVisible ? panelH : 0;
  const timelineVisible = registered
    ? liveTimelineVisible
    : gapRef.current.timelineVisible;
  const panelFootprint = registered
    ? livePanelFootprint
    : gapRef.current.panelFootprint;
  const sectionSlot = registered
    ? pageSection
    : gapRef.current.hadSection
      ? PAGE_SECTION_PLACEHOLDER
      : null;
  // The bar's CONTENT is held too, not only the geometry around it: these five
  // fields decide the row's SHAPE. Controls are self-sized in the one-row bar
  // (only the search input flexes), so the equal-split coupling is gone, but a
  // field that appears or disappears mid-gap still shifts everything after it
  // in the row — selectorExtras is a direct child (chips), controlsExtra is a
  // whole control and presetProps/searchProps size two core ones. The override
  // stands in for the event-type control; it is held so the row cannot flash
  // the core selector, under the leaving page's data, on the way out.
  // Held nodes carry the exiting page's handlers: a click inside the gap still
  // writes the LENS (which the arriving page reads back), and the part that
  // cannot land is the page's own echo — a no-op on an unmounted tree.
  // The other core-control seams (eventType/region sources + selector props,
  // placeholder, loading) are deliberately NOT held: they feed the CORE change
  // handlers above (a held `sources` list with a live empty one behind it would
  // make a gap click read `next.length >= 0` and CLEAR the dimension), and they
  // change content only — child count and widths are identical either way, so
  // the instant-swap ruling is satisfied without them.
  const heldBar = registered ? null : gapRef.current.bar;
  const barSelectorOverride = heldBar ? heldBar.selectorOverride : selectorOverride;
  const barControlsExtra = heldBar ? heldBar.controlsExtra : controlsExtra;
  const barPresetProps = heldBar ? heldBar.presetProps : presetProps;
  const barSearchProps = heldBar ? heldBar.searchProps : searchProps;
  const barSelectorExtras = heldBar ? heldBar.selectorExtras : selectorExtras;
  const barSelectorLead = heldBar ? heldBar.selectorLead : selectorLead;
  // COMMITTED renders only. Written in render, a concurrent render React throws
  // away would leave the gap holding geometry that never painted.
  useLayoutEffect(() => {
    if (!registered) return;
    gapRef.current = {
      hadSection: pageSection != null,
      timelineVisible: liveTimelineVisible,
      panelFootprint: livePanelFootprint,
      bar: {
        selectorOverride,
        controlsExtra,
        presetProps,
        searchProps,
        selectorExtras,
        selectorLead,
      },
    };
  });
  const topInset = NAV_CLEARANCE + topBarH + TOOLBAR_GAP;
  const bottomInset =
    panelFootprint + (timelineVisible ? timelineH : 0) + SCENE_GAP;

  const frame = useMemo(
    () => ({
      width: dims.width,
      height: dims.height,
      topBarH,
      panelH,
      panelFootprint,
      panelMode,
      setPanelMode,
      timelineVisible,
      topInset,
      bottomInset,
    }),
    [dims, topBarH, panelH, panelFootprint, panelMode, setPanelMode,
      timelineVisible, topInset, bottomInset],
  );

  // The provider's activePreset is {kind, id} — the dynamic lenses (trending,
  // and later latest/recommended, §46.3 #3) carry a null id and are addressed
  // by kind in the rows. A page may override any of this via presetProps.
  const lensControlProps = {
    presets: presets || [],
    activePresetId:
      activePreset && typeof activePreset === "object"
        ? (activePreset.id ?? (activePreset.kind !== "draft" ? activePreset.kind : null))
        : (activePreset ?? null),
    isDraft: activePreset?.kind === "draft",
    isModified: !!modified,
    ...(barPresetProps || {}),
  };
  // New/Save are BAR buttons (SIDEBAR_LENS_REWORK, birr 2026-08-21 —
  // supersedes the 08-19 dropdown-footer ruling): peeled off the bag here so
  // LensControl's menu never sees them. The handlers are page-registered
  // (presetProps) and gap-held like the rest of the bag.
  const {
    onSave: onSaveLens,
    onNew: onNewLens,
    canSave = false,
    ...controlProps
  } = lensControlProps;

  return (
    <LensChromeContext.Provider value={registrar}>
      <LensFrameContext.Provider value={frame}>
        <div
          ref={rootRef}
          data-lens-shell="p59"
          className={`relative isolate h-full w-full overflow-hidden font-poppins ${className}`}
        >
          {children}

          {/* ONE ROW (2026-08-19 restyle): lens · filters · page section ·
              extras · search · refresh. Controls take their natural width —
              the two core selectors get a fixed basis they may shrink from —
              and the SEARCH INPUT is the row's only flexer, absorbing the
              slack that used to be a whole second row's divider and padding.
              LensToolbar's row container is flex-wrap, so a viewport too
              narrow for one line wraps instead of clipping, and the measured
              bar height carries the wrap into topInset. */}
          <LensToolbar
            ref={topBarRef}
            rowRef={rowRef}
            row={
              <>
                {isGSVariant && <LensControl {...controlProps} />}
                {/* New/Save: icon-only, lens-control language (the
                    LensViewportControls BUTTON_CLASS treatment). Save acts
                    on the same state the trigger's amber dot signals. */}
                {isGSVariant && (
                <>
                <button
                  type="button"
                  onClick={() => onNewLens?.()}
                  aria-label="New lens"
                  title="New lens (resets filters)"
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-xs text-mutedText transition-all duration-200 hover:bg-mainButtonBg/60 hover:text-mainText focus:outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60"
                >
                  <Plus size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => onSaveLens?.()}
                  disabled={!canSave}
                  aria-label="Save lens"
                  title={canSave ? "Save lens" : "No changes to save"}
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-xs text-mutedText transition-all duration-200 hover:bg-mainButtonBg/60 hover:text-mainText focus:outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-mutedText"
                >
                  <Save size={14} />
                </button>
                </>
                )}
                {/* An override (the trending row) stands in for the EVENT-TYPE
                    control and for nothing else: trending resolves event types
                    only, so every other control keeps rendering beside it. A
                    filter that is applied has to stay reachable — a region or a
                    curation the user cannot see is a filter running blind. */}
                {/* The cell and its lead share a wrapper, so the row reads as
                    one unit in both branches. The lead LEADS (birr 2026-08-17):
                    it stays beside the trending-types text it governs. The
                    selector branch takes a fixed shrinkable basis (w-44); the
                    override pill sizes naturally — with self-sized controls no
                    other control's width depends on which branch renders. */}
                {isGSVariant && (
                <div className="flex min-w-0 shrink items-center gap-2">
                  {barSelectorLead}
                  {barSelectorOverride || (
                    <div className="flex w-44 min-w-0 items-stretch">
                      <SourceSelector
                        allLabel="All event types"
                        emptyLabel="All event types"
                        countsReady={false}
                        emptyMeansAll
                        icon={MapPin}
                        isPageRow={isFeedViewPageRow}
                        onChange={handleEventTypesChange}
                        selectedSources={selectedEventTypes}
                        sources={eventTypeSources}
                        summaryLabel="event types"
                        title="Event types"
                        {...(eventTypeSelectorProps || {})}
                      />
                    </div>
                  )}
                </div>
                )}
                {isGSVariant && (
                <div className="flex w-40 min-w-0 items-stretch">
                  <SourceSelector
                    allLabel="All regions"
                    emptyLabel="All regions"
                    countsReady={false}
                    emptyMeansAll
                    icon={Globe}
                    onChange={handleRegionsChange}
                    onSearchChange={onRegionSearchChange}
                    selectedSources={selectedRegions}
                    sources={regionSources}
                    summaryLabel="regions"
                    title="Regions"
                    {...(regionSelectorProps || {})}
                  />
                </div>
                )}
                <CurationSelector
                  value={curation || null}
                  onChange={handleCurationChange}
                />
                {/* Page section: the shared core ends at this divider. During a
                    route-transition gap the WRAPPER stays (with a fixed-width
                    placeholder inside) so the bar cannot reflow while nothing
                    is registered. */}
                {sectionSlot != null && (
                  <div className="flex min-w-0 items-stretch gap-2 border-l border-mainDivider pl-2">
                    {sectionSlot}
                  </div>
                )}
                {barSelectorExtras}
                {/* The row's one flexer, in a wrapper the split logic can
                    measure and move: split, it takes `basis-full` and
                    `order-last` — with the row's flex-wrap that IS the
                    designed second row; inline, `flex-1` hands the input
                    the slack exactly as before (its own min-w-[160px]
                    still sets the wrap floor). */}
                <div
                  ref={searchWrapRef}
                  className={
                    searchSplit
                      ? "order-last flex basis-full"
                      : "flex flex-1"
                  }
                >
                  <LensSearchInput
                    value={search || ""}
                    onChange={handleSearchChange}
                    onKeyDown={(event) => {
                      // The value the INPUT holds, not the provider's: a page
                      // that overrides `value` through searchProps would
                      // otherwise submit a string the user never typed.
                      if (event.key === "Enter")
                        onSearchSubmit?.(event.target.value ?? "");
                    }}
                    placeholder={searchPlaceholder}
                    loading={searchLoading && !!search}
                    {...(barSearchProps || {})}
                  />
                </div>
                {barControlsExtra}
              </>
            }
          />

          {/* The BAND is reserved by `timelineVisible` (which holds through a
              gap); the strip itself renders only against a live bag. */}
          {liveTimelineVisible && (
            <TimelineStrip
              bottomOffset={panelFootprint}
              customRange={customDateRange}
              events={timeline.events || []}
              onCustomRangeApply={handleCustomRangeApply}
              onHeightChange={handleTimelineHeight}
              onPlaybackChange={handlePlaybackReport}
              onSelectEvent={timeline.onSelectEvent}
              onTimeRangeChange={handleTimeRangeChange}
              playbackStopSignal={timeline.playbackStopSignal || 0}
              rangeLocked={!!timeline.rangeLocked}
              selectedEvent={timeline.selectedEvent || null}
              serverTimeline={timeline.serverTimeline || null}
              showPlayback={timeline.showPlayback !== false}
              timeRange={timeRange}
            />
          )}

          {panelEnabled && (
            <PanelFrame
              mode={panelMode}
              onModeChange={setPanelMode}
              visible={panelVisible}
              header={panelHeader}
              content={panelContent}
              staticBar={panelStatic && panelContent == null}
              scope={panelScope}
              onHeightChange={setPanelH}
              onCameraShift={onPanelCameraShift}
            />
          )}
        </div>
      </LensFrameContext.Provider>
    </LensChromeContext.Provider>
  );
};

export default LensShell;
